"use client";

import {JSX, memo} from "react";

import {type CalendarDay, type MonthData} from "@/shared/models/form/datepickers";
import {Typography} from "@/shared/ui/data-display/typography";

import {CalendarDayItem} from "../parts/calendar-day-item";

export interface CalendarMonthViewProps {
    monthData: MonthData;
    onDayClick: (day: CalendarDay, element: HTMLDivElement) => void;
    onDayHover: (day: CalendarDay, isDisabled: boolean, index: number, element: HTMLDivElement) => void;
}

// Compare two days for equality
function areDaysEqual(prevDay: CalendarDay, nextDay: CalendarDay): boolean {
    return (
        prevDay.date.getTime() === nextDay.date.getTime() &&
        prevDay.day === nextDay.day &&
        prevDay.isCurrentMonth === nextDay.isCurrentMonth &&
        prevDay.isToday === nextDay.isToday &&
        prevDay.isStartDate === nextDay.isStartDate &&
        prevDay.isEndDate === nextDay.isEndDate &&
        prevDay.isInRange === nextDay.isInRange &&
        prevDay.isInHoverRange === nextDay.isInHoverRange &&
        prevDay.isHoverBefore === nextDay.isHoverBefore &&
        prevDay.isDisabled === nextDay.isDisabled
    );
}

export const CalendarMonthView = memo(
    function CalendarMonthView({monthData, onDayClick, onDayHover}: CalendarMonthViewProps): JSX.Element {
        // Calculate empty cells needed at the start (Monday = 0, Sunday = 6)
        const firstDay = monthData.days[0]?.date;
        const dayOfWeek = firstDay ? (firstDay.getDay() + 6) % 7 : 0;

        return (
            <div className="scrollable-calendar__month">
                <Typography as="h3" size={16} weight={600} color="title" className="scrollable-calendar__month-title">
                    {monthData.monthName}
                </Typography>

                <div className="scrollable-calendar__days">
                    {/* Empty cells for alignment */}
                    {Array.from({length: dayOfWeek}).map((_, i) => (
                        <div key={`empty-${i}`} className="scrollable-calendar__day--empty" />
                    ))}

                    {monthData.days.map((day, index) => (
                        <CalendarDayItem
                            key={index}
                            day={day}
                            index={index}
                            isDisabled={day.isDisabled}
                            additionalClassName={null}
                            onDayClick={onDayClick}
                            onMouseEnter={onDayHover}
                        />
                    ))}
                </div>
            </div>
        );
    },
    (prevProps, nextProps) => {
        // Callbacks are stable via autoBind - compare only month data
        const prevMonth = prevProps.monthData;
        const nextMonth = nextProps.monthData;

        if (prevMonth.month !== nextMonth.month) return false;
        if (prevMonth.year !== nextMonth.year) return false;
        if (prevMonth.days.length !== nextMonth.days.length) return false;

        // Compare each day - return false (re-render) if any day changed
        for (let i = 0; i < prevMonth.days.length; i++) {
            if (!areDaysEqual(prevMonth.days[i], nextMonth.days[i])) {
                return false;
            }
        }

        return true;
    },
);
